// Information and system risk for the EPM scorecard: where information leaves the systems that should hold it. Three
// patterns are read from the process:
//
//   - a manual bridge: work in a system passes, by export, by hand or through a local tool (a spreadsheet, email), to
//     work in another system, or back into the same one. The steps between can introduce errors and leave stale copies
//     behind, and the time they take is also friction, so the bridge appears under both, and its time is
//     counted once in the friction total
//   - an unofficial copy: an output made by hand or in a local tool, or kept as a file or on paper, that other work goes
//     on to use. It can be found and used as if it were authoritative when it isn't
//   - important information in a local tool: a local tool doing work across several models, or holding information of
//     high value, with no controls around what is in it
//
// The information's own type sharpens all three: information handed on as a file, on paper or by word of mouth isn't
// in any system, whatever the steps either side are done in. A step that takes paper or spoken information and types it
// into a system is re-keying it, which is friction (counted in the scorecard's friction total) as well as a risk.
//
// Pure, so it can be tested without a page
import { AnalysisEdge, AnalysisNode, isUseOf } from '../common/analysis-model';
import { NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY, SYSTEM_KIND, WORK_TYPE } from '../common/linqset-model';
import { MEDIUM_TYPES, SYSTEM_TYPES } from '../common/information-value';

// work whose whole time is friction as exception work (or on a detour), and work said not to add value, whose time a
// bridge or re-keying counts where the step is one, as they say why it adds nothing
const onException = (step: AnalysisNode) => !!(step.exceptionWork || step.onDetour);
const notValueAdding = (step: AnalysisNode) => step.workKind === WORK_TYPE.NOT_VALUE_ADDING;

export type InformationRiskKind = 'bridge' | 'copy' | 'local';

// information held outside any system as it passes, most at risk first: spoken has no record at all, paper can be lost
// and has no trail, a file is copied freely
interface Medium { rank: number; held: string; handed: string; via: string }
const MEDIA: Partial<Record<NODE_TYPE, Medium>> = {
    [NODE_TYPE.VERBAL]: { rank: 3, held: 'passed on by word of mouth, with no record', handed: 'passed on by phone or in person', via: 'by word of mouth' },
    [NODE_TYPE.PAPER]: { rank: 2, held: 'kept on paper', handed: 'printed and handled on paper', via: 'on paper' },
    [NODE_TYPE.FILE]: { rank: 1, held: 'kept as a file', handed: 'exported to a file', via: 'as a file' },
};
export const mediumOf = (type: NODE_TYPE | undefined) => (type === undefined ? undefined : MEDIA[type]);
// paper and spoken information has to be typed in to reach a system: re-keying
const REKEYED = new Set([NODE_TYPE.PAPER, NODE_TYPE.VERBAL]);
// what says nothing about how information is held
const GENERAL_TYPES = new Set([NODE_TYPE.INFORMATION, undefined]);
// physical devices nothing is typed into: signing with a pen or scanning a page isn't re-keying
const DEVICE = /\b(pens?|printers?|scanners?|cameras?|fax)\b/i;
// channels that carry information as it's typed: a call is spoken, a printout paper, an email attachment a file
const CHANNEL = /\b(phones?|telephones?|mobiles?|voicemail|e-?mails?|outlook|gmail|printers?|scanners?|fax|post)\b/i;

// a step typing paper or spoken information into a system or a digital tool
export interface EpmRekeyRow {
    key: string;
    step: string;
    stepKey: string;
    modelKey: string;
    information: string;            // what it types in
    medium: string;                 // paper, or word of mouth
    systems: string;
    model: string;
    items: number;
    hours: number;                  // staff time a year spent typing it in
    cost: number;
}

export interface EpmInformationRiskRow {
    key: string;                    // unique, without a colon: it is also the key of its signal
    kind: InformationRiskKind;
    title: string;
    detail: string;
    model: string;
    severity: 'high' | 'medium';
    hours: number;                  // a year: of the manual steps on a bridge, of the work in a local tool
    cost: number;
    alsoFriction: boolean;          // a bridge's time is friction too
    frictionHours?: number;         // a bridge: what it adds to friction, less time counted elsewhere or by another bridge
    frictionCost?: number;
    systemsSuggested: boolean;      // a system's kind it rests on is LINQ's read of its name, not confirmed
    context: { kind: 'action' | 'output' | 'system'; key: string; modelKey: string };   // what its context graph shows, and the model it is in
    systems: string[];              // the systems whose kind it rests on, to correct
    information?: { key: string; modelKey: string; type?: NODE_TYPE; read?: NODE_TYPE };   // a copy: the information, whose type says how it is held, and LINQ's read of it where it's typed as plain information
}

export interface InformationRiskInput {
    actions: AnalysisNode[];
    outputs: AnalysisNode[];
    edges: AnalysisEdge[];
    systems: { key: string; name: string; kind: SYSTEM_KIND; confirmed: boolean }[];
    externalRoleKeys: Set<string>;
    modelName: (key: string) => string;
    number: (v: number) => string;
    money: (v: number) => string;
}

// where an action's work is done: in a system of record or a business system, only in local tools, by hand with no
// system, or by no one of ours (external roles, or no one assigned)
type Place = 'record' | 'business' | 'local' | 'manual' | 'none';

// the most steps a bridge can take between systems before it isn't read as one hand-off
const MAX_BRIDGE_STEPS = 4;
// a local tool used in this many models is doing organisation-wide work
const LOCAL_TOOL_MODELS = 2;
// information of at least this derived value (0 to 100) is among the organisation's most valuable
const HIGH_DERIVED_VALUE = 50;

const list = (names: string[], max = 3) => names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} and ${names.length - max} more`;

export const buildInformationRisks = (input: InformationRiskInput) => {
    const { actions, outputs, edges, externalRoleKeys, modelName, number, money } = input;
    const systemByKey = new Map(input.systems.map(s => [s.key, s]));
    const actionByKey = new Map(actions.map(a => [a._key, a]));
    const systemsOf = (a: AnalysisNode) => [...new Set([...(a.systems || []), ...(a.roles || []).filter(r => !externalRoleKeys.has(r._key)).flatMap(r => r.systemKeys || [])])]
        .filter(k => systemByKey.has(k));
    const staffed = (a: AnalysisNode) => (a.roles || []).some(r => !externalRoleKeys.has(r._key)) || (a.systems || []).length > 0;
    const placeOf = (a: AnalysisNode): Place => {
        if (!staffed(a)) return 'none';
        const kinds = systemsOf(a).map(k => systemByKey.get(k)!.kind);
        if (kinds.includes(SYSTEM_KIND.RECORD)) return 'record';
        if (kinds.includes(SYSTEM_KIND.BUSINESS)) return 'business';
        return kinds.length ? 'local' : 'manual';
    };
    const inSystem = (p: Place) => p === 'record' || p === 'business';
    const offSystem = (p: Place) => p === 'local' || p === 'manual';
    // a step no one does, where items wait (to be picked up, say): a bridge passes through it, as the information is
    // still outside any system while it waits. A step an external role does isn't one: the work has left us
    const waiting = (a: AnalysisNode) => !(a.roles || []).length && !(a.systems || []).length;
    const outgoing = new Map<string, AnalysisEdge[]>();
    const incoming = new Map<string, AnalysisEdge[]>();
    edges.forEach(e => {
        (outgoing.get(e.from) || outgoing.set(e.from, []).get(e.from)!).push(e);
        (incoming.get(e.to) || incoming.set(e.to, []).get(e.to)!).push(e);
    });
    const systemsIn = (a: AnalysisNode, kinds: Place[]) => systemsOf(a).filter(k => kinds.includes(systemByKey.get(k)!.kind as Place));
    const named = (keys: string[]) => list(keys.map(k => systemByKey.get(k)?.name || 'Unnamed'));
    const suggested = (keys: string[]) => keys.some(k => !systemByKey.get(k)?.confirmed);
    // the staff's time in a step spent on what arrives along one connection: its share of the step's items, and the
    // staff's share of the step (external roles' time isn't ours)
    const staffShare = (a: AnalysisNode) => {
        const roles = a.roles || [];
        return roles.length ? roles.filter(r => !externalRoleKeys.has(r._key)).length / roles.length : 0;
    };
    const handling = (e: AnalysisEdge, to: AnalysisNode) => {
        const share = to.items ? Math.min(1, (e.items || 0) / to.items) : 1;
        return { hours: (to.hours || 0) * staffShare(to) * share, cost: (to.cost || 0) * staffShare(to) * share };
    };
    const rows: EpmInformationRiskRow[] = [];

    // ----- manual bridges: from work in a system, through steps outside systems (or handed on as a file, on paper or by
    // word of mouth), into work in a system -----
    const bridges = new Map<string, { from: AnalysisNode; to: AnalysisNode; via: AnalysisNode[]; info: string; items: number; medium?: Medium; handled: { hours: number; cost: number } }>();
    actions.filter(a => inSystem(placeOf(a))).forEach(from => {
        (outgoing.get(from._key) || []).forEach(first => {
            const start = actionByKey.get(first.to);
            if (!start) return;
            const medium = mediumOf(first.informationType);
            // handed straight from one system's step to another's, but not in any system on the way
            if (inSystem(placeOf(start)) && medium) {
                const key = `${from._key}>${start._key}`;
                if (!bridges.has(key)) bridges.set(key, { from, to: start, via: [], info: first.informationName || 'information', items: first.items || 0, medium, handled: handling(first, start) });
                return;
            }
            if (!offSystem(placeOf(start))) return;
            // follow the work outside systems until it comes back into one, nearest first
            const queue: { at: AnalysisNode; via: AnalysisNode[] }[] = [{ at: start, via: [start] }];
            const seen = new Set([start._key]);
            while (queue.length) {
                const { at, via } = queue.shift()!;
                (outgoing.get(at._key) || []).forEach(e => {
                    const next = actionByKey.get(e.to);
                    if (!next || seen.has(next._key)) return;
                    seen.add(next._key);
                    const place = placeOf(next);
                    if (inSystem(place)) {
                        const key = `${from._key}>${next._key}`;
                        if (!bridges.has(key)) bridges.set(key, { from, to: next, via, info: first.informationName || 'information', items: first.items || 0, medium, handled: { hours: 0, cost: 0 } });
                    } else if (offSystem(place) && via.length < MAX_BRIDGE_STEPS) queue.push({ at: next, via: [...via, next] });
                    // waiting takes no work, so it isn't one of the bridge's steps
                    else if (waiting(next)) queue.push({ at: next, via });
                });
            }
        });
    });
    // one row for each route: from the same systems, through the same tools, into the same systems, however many places
    // in the process take it. The steps between are counted once, though several hand-offs may share them
    const routes = new Map<string, { key: string; title: string; fromSystems: string[]; toSystems: string[]; locals: string[];
        hops: { from: AnalysisNode; to: AnalysisNode; info: string; items: number; medium?: Medium; handled: { hours: number; cost: number } }[]; via: Map<string, AnalysisNode> }>();
    bridges.forEach(({ from, to, via, info, items, medium, handled }, key) => {
        const fromSystems = systemsIn(from, ['record', 'business']);
        const toSystems = systemsIn(to, ['record', 'business']);
        const sameSystem = fromSystems.some(k => toSystems.includes(k));
        const locals = [...new Set(via.flatMap(v => systemsIn(v, ['local'])))];
        const through = locals.length ? `through ${named(locals)}` : via.length ? 'by hand' : medium!.via;
        const title = sameSystem ? `${info} leaves ${named(fromSystems)} and comes back ${through}` : `${named(fromSystems)} to ${named(toSystems)} ${through}`;
        const route = routes.get(title) || routes.set(title, { key, title, fromSystems, toSystems, locals, hops: [], via: new Map() }).get(title)!;
        route.hops.push({ from, to, info, items, medium, handled });
        via.forEach(v => route.via.set(v._key, v));
    });
    // the bridges' time as friction: only the steps on them said not to add value. A bridge is always a risk, but work on
    // it that adds value (booking a carrier by phone, planning in a spreadsheet) is still the work, not waste, however it's
    // done. A step not adding value is the bridge's, for its whole time, and not counted again under not value adding, as
    // the bridge says why it adds nothing and what to do about it. Exception work is counted there. A step several routes
    // go through is counted once
    const bridgeWork = new Map<string, { step: AnalysisNode; hours: number; cost: number }>();
    const claim = (step: AnalysisNode) => {
        if (onException(step) || !notValueAdding(step)) return;
        bridgeWork.set(step._key, { step, hours: step.hours || 0, cost: step.cost || 0 });
    };
    // each route's work, step by step, to share the bridges' friction out between them
    const bridgeParts = new Map<string, AnalysisNode[]>();
    routes.forEach(({ key, title, fromSystems, toSystems, locals, hops, via }) => {
        const intoRecord = toSystems.some(k => systemByKey.get(k)!.kind === SYSTEM_KIND.RECORD);
        const fromRecord = fromSystems.some(k => systemByKey.get(k)!.kind === SYSTEM_KIND.RECORD);
        const steps = [...via.values()];
        const media = [...new Set(hops.map(h => h.medium).filter((m): m is Medium => !!m))].sort((a, b) => b.rank - a.rank);
        // the work of the hand-off: the steps between, or for a direct hand-off, taking it in at the other end
        const hours = steps.reduce((t, v) => t + (v.hours || 0), 0) + hops.reduce((t, h) => t + h.handled.hours, 0);
        const cost = steps.reduce((t, v) => t + (v.cost || 0), 0) + hops.reduce((t, h) => t + h.handled.cost, 0);
        steps.forEach(claim);
        hops.forEach(h => claim(h.to));
        bridgeParts.set(`bridge-${key}`, [...steps, ...hops.map(h => h.to)].filter((s, n, all) => all.indexOf(s) === n));
        // the steps on it that don't add value, whose time is friction
        const waste = [...steps, ...hops.map(h => h.to)].filter((s, n, all) => all.indexOf(s) === n && notValueAdding(s) && !onException(s));
        const wasteHours = waste.reduce((t, s) => t + (s.hours || 0), 0);
        const wasteCost = waste.reduce((t, s) => t + (s.cost || 0), 0);
        const [hop] = hops;
        const items = hops.reduce((t, h) => t + h.items, 0);
        const how = media.length ? `, ${list(media.map(m => m.handed))}` : '';
        const what = !steps.length
            ? `${number(items)} items a year of ${list([...new Set(hops.map(h => h.info))])} leave ${hop.from.name} in ${named(fromSystems)}${how}, then ${hop.to.name} takes ${hops.length === 1 ? 'them' : 'what arrives'} into ${named(toSystems)}${hops.length > 1 ? `, in ${hops.length} places` : ''}.`
            : hops.length === 1
            ? `${number(items)} items a year of ${hop.info} leave ${hop.from.name} in ${named(fromSystems)}${how}, go through ${list(steps.map(v => v.name))} ${locals.length ? `in ${named(locals)}` : 'with no system'}, then into ${hop.to.name} in ${named(toSystems)}.`
            : `In ${hops.length} places, information leaves ${named(fromSystems)}${how} for ${list(steps.map(v => v.name))} ${locals.length ? `in ${named(locals)}` : 'with no system'}, then goes into ${named(toSystems)}: ${number(items)} items a year, among them ${list([...new Set(hops.map(h => h.info))])}.`;
        rows.push({
            key: `bridge-${key}`, kind: 'bridge', model: [...new Set(hops.map(h => modelName(h.from.modelKey)))].join(', '),
            severity: intoRecord || fromRecord || (media[0]?.rank || 0) >= 2 ? 'high' : 'medium',
            title: hops.length > 1 ? `${title} (${hops.length} places)` : title,
            detail: `${what} Each step outside a system can bring in errors, and can leave copies that go out of date${intoRecord ? ', here loaded into a system of record' : ''}. `
                + (hours > 0 ? `That work takes ${number(hours)} hours a year${cost > 0 ? ` (${money(cost)})` : ''}` : 'It takes no staff time, but the copy it leaves is still a risk.')
                + (wasteHours > 0 ? `, of which ${list(waste.map(s => s.name))} ${waste.length === 1 ? "doesn't" : "don't"} add value: ${number(wasteHours)} hours${wasteCost > 0 ? ` (${money(wasteCost)})` : ''}, which is friction too.` : hours > 0 ? '.' : ''),
            hours, cost, alsoFriction: wasteHours > 0, systemsSuggested: suggested([...fromSystems, ...toSystems, ...locals]),
            context: { kind: 'action', key: (steps[0] || hop.to)._key, modelKey: (steps[0] || hop.to).modelKey },
            systems: [...new Set([...fromSystems, ...toSystems, ...locals])],
        });
    });

    // ----- unofficial copies: outputs made outside systems, or kept as files or paper, that other work uses -----
    const creatorsOf = (o: AnalysisNode) => (incoming.get(o._key) || []).map(e => actionByKey.get(e.from)).filter((a): a is AnalysisNode => !!a);
    outputs.filter(o => o.superType === NODE_SUPERTYPE.INFORMATION).forEach(o => {
        const usedBy = (outgoing.get(o._key) || []).filter(e => isUseOf(e, o));
        const used = (o.consumers || 0) > 0 || usedBy.length > 0;
        if (!used) return;
        const creators = creatorsOf(o);
        const kept = mediumOf(o.heldAs ?? o.type);
        const madeOff = creators.length > 0 && creators.every(a => offSystem(placeOf(a)));
        if (!kept && !madeOff) return;
        const locals = [...new Set(creators.flatMap(a => systemsIn(a, ['local'])))];
        const where = kept ? kept.held : locals.length ? `made in ${named(locals)}` : 'made by hand, outside any system';
        const uses = Math.max(o.consumers || 0, usedBy.length);
        const valuable = (o.value || 0) > 0 || (o.revenue || 0) > 0 || (o.derivedValue || 0) >= HIGH_DERIVED_VALUE;
        rows.push({
            key: `copy-${o._key}`, kind: 'copy', model: modelName(o.modelKey),
            severity: valuable || (o.consumerModels || 0) > 0 ? 'high' : 'medium',
            title: `${o.name} is ${where}`,
            detail: `${o.name} is ${where}, not in a system of record, yet ${uses === 1 ? 'a step goes on to use it' : `${uses} steps go on to use it`}${(o.consumerModels || 0) > 0 ? `, in ${o.consumerModels} other model${o.consumerModels === 1 ? '' : 's'} too` : ''}. `
                + `A copy like this can be found and used as if it were the authoritative one, when it isn't.${valuable ? ' It carries value, so a wrong copy costs more.' : ''}`,
            hours: creators.reduce((t, a) => t + (a.hours || 0), 0), cost: creators.reduce((t, a) => t + (a.cost || 0), 0),
            alsoFriction: false, systemsSuggested: suggested(locals), context: { kind: 'output', key: o._key, modelKey: o.modelKey },
            systems: locals, information: { key: o._key, modelKey: o.modelKey, type: o.type, read: o.heldAsRead ? o.heldAs : undefined },
        });
    });

    // ----- unofficial copies along the way: information passed between steps as a file, on paper or by word of mouth,
    // that later steps work from. One row for each piece of information, however many steps use it -----
    const outputKeys = new Set(outputs.map(o => o._key));
    const passed = new Map<string, { name: string; medium: Medium; type?: NODE_TYPE; users: Set<string>; models: Set<string>; first: string; modelKey: string }>();
    edges.forEach(e => {
        const medium = mediumOf(e.informationType);
        const user = actionByKey.get(e.to);
        if (!medium || !user || !e.informationKey || outputKeys.has(e.informationKey) || !staffed(user)) return;
        const p = passed.get(e.informationKey) || passed.set(e.informationKey, { name: e.informationName || 'Information', medium, type: e.informationType, users: new Set(), models: new Set(), first: user._key,
            // information belongs to the model of the step that creates it
            modelKey: actionByKey.get(e.from)?.modelKey || user.modelKey }).get(e.informationKey)!;
        p.users.add(user._key);
        p.models.add(user.modelKey);
    });
    passed.forEach((p, key) => {
        const users = [...p.users].map(k => actionByKey.get(k)!);
        rows.push({
            key: `copy-${key}`, kind: 'copy', model: list([...p.models].map(modelName)),
            severity: p.medium.rank >= 2 || users.length > 1 ? 'high' : 'medium',
            title: `${p.name} is ${p.medium.held}`,
            detail: `${p.name} is ${p.medium.held}, not in a system of record, yet ${users.length === 1 ? `${users[0].name} works from it` : `${users.length} steps work from it (${list(users.map(u => u.name))})`}. `
                + `A copy like this can be found and used as if it were the authoritative one, when it isn't.`,
            hours: users.reduce((t, a) => t + (a.hours || 0), 0), cost: users.reduce((t, a) => t + (a.cost || 0), 0),
            alsoFriction: false, systemsSuggested: false, context: { kind: 'action', key: p.first, modelKey: actionByKey.get(p.first)!.modelKey },
            systems: [], information: { key, modelKey: p.modelKey, type: p.type },
        });
    });

    // ----- re-keying: paper or spoken information typed into a system or a digital tool (a spreadsheet, a document).
    // Friction, counted in its total; exception work is left out here, as its time is friction there. A step said not to
    // add value is re-keying's: its whole time is counted here, and not again under not value adding, as re-keying says
    // why it adds nothing and what to do about it -----
    const rekeyed = new Map<string, EpmRekeyRow & { infos: Set<string>; media: Set<string> }>();
    edges.forEach(e => {
        const to = actionByKey.get(e.to);
        if (!to || !REKEYED.has(e.informationType as NODE_TYPE) || !(inSystem(placeOf(to)) || placeOf(to) === 'local') || !systemsOf(to).some(k => !DEVICE.test(systemByKey.get(k)!.name)) || !staffShare(to) || onException(to)) return;
        const { hours, cost } = notValueAdding(to) ? { hours: 0, cost: 0 } : handling(e, to);
        const r = rekeyed.get(to._key) || rekeyed.set(to._key, {
            key: `rekey-${to._key}`, step: to.name, stepKey: to._key, modelKey: to.modelKey, information: '', medium: '', systems: named(systemsIn(to, ['record', 'business', 'local'])),
            model: modelName(to.modelKey), items: 0, hours: 0, cost: 0, infos: new Set(), media: new Set(),
        }).get(to._key)!;
        r.items += e.items || 0;
        r.hours += hours;
        r.cost += cost;
        r.infos.add(e.informationName || 'information');
        r.media.add(e.informationType === NODE_TYPE.VERBAL ? 'word of mouth' : 'paper');
    });
    const rekeying: EpmRekeyRow[] = [...rekeyed.values()].map(({ infos, media, ...r }) => {
        const step = actionByKey.get(r.stepKey)!;
        const whole = notValueAdding(step) ? { hours: step.hours || 0, cost: step.cost || 0 } : {};
        return { ...r, ...whole, information: list([...infos]), medium: [...media].join(' and ') };
    })
        .filter(r => r.hours > 0).sort((a, b) => b.hours - a.hours);

    // what the bridges add to friction: their steps' time, less what re-keying already counts in the same step (typing a
    // paper hand-off into a system is both, and is counted once)
    const rekeyedAt = new Map(rekeying.map(r => [r.stepKey, r]));
    const bridgeFriction = [...bridgeWork.values()].map(w => ({
        key: w.step._key,
        hours: Math.max(0, w.hours - (rekeyedAt.get(w.step._key)?.hours || 0)),
        cost: Math.max(0, w.cost - (rekeyedAt.get(w.step._key)?.cost || 0)),
    }));
    // what each bridge adds to friction, so they add up to the bridges' total: not the time counted as re-keying, and a
    // step several bridges share given to the costliest of them
    const left = new Map(bridgeFriction.map(f => [f.key, f]));
    rows.filter(r => r.kind === 'bridge').sort((a, b) => b.cost - a.cost || b.hours - a.hours).forEach(r => {
        r.frictionHours = 0;
        r.frictionCost = 0;
        (bridgeParts.get(r.key) || []).forEach(step => {
            const share = left.get(step._key);
            if (!share) return;
            left.delete(step._key);
            r.frictionHours! += share.hours;
            r.frictionCost! += share.cost;
        });
    });

    // ----- what the information's type can't yet say: hand-offs left as the general type, with no system attached to
    // say they are held in it -----
    const handoffTypes = new Map<string, { type?: NODE_TYPE; inSystem: boolean; name: string; modelKey: string }>();
    edges.forEach(e => {
        // information belongs to the model of the step that creates it
        if (e.informationKey && actionByKey.has(e.from) && actionByKey.has(e.to))
            handoffTypes.set(e.informationKey, { type: e.informationType, inSystem: !!e.informationInSystem, name: e.informationName || 'Information', modelKey: actionByKey.get(e.from)!.modelKey });
    });
    const untypedHandoffItems = [...handoffTypes.entries()].filter(([, h]) => GENERAL_TYPES.has(h.type) && !h.inSystem).map(([key, h]) => ({ key, name: h.name, modelKey: h.modelKey }));
    const untypedHandoffs = untypedHandoffItems.length;

    // ----- types that don't match the systems around the information: typed as held in a system, yet no system is
    // attached to it and it's made by hand; or typed as a file, on paper or by word of mouth, yet a system of record or
    // business system supplies it (a channel such as a phone, a printer or email carries it that way, so isn't one).
    // Either the type or the model is wrong, and both the friction and the risk read rest on them -----
    const typeConflicts = new Map<string, { key: string; name: string; modelKey: string; reason: string }>();
    const checkType = (key: string, name: string, type: NODE_TYPE | undefined, systems: string[], makers: AnalysisNode[]) => {
        if (typeConflicts.has(key) || type === undefined) return;
        const held = makers[0]?.modelKey;
        if (SYSTEM_TYPES.has(type) && !systems.length && makers.length && makers.every(m => placeOf(m) === 'manual')) {
            typeConflicts.set(key, { key, name, modelKey: held!, reason: 'typed as held in a system, yet no system is attached to it and it is made by hand' });
        } else if (MEDIUM_TYPES.has(type)) {
            const holding = systems.filter(k => { const s = systemByKey.get(k); return !!s && s.kind !== SYSTEM_KIND.LOCAL && !CHANNEL.test(s.name); });
            if (holding.length) typeConflicts.set(key, { key, name, modelKey: held!, reason: `typed as ${mediumOf(type)!.via.replace(/^(as|on|by) /, '')}, yet ${named(holding)} supplies it` });
        }
    };
    edges.forEach(e => {
        const from = actionByKey.get(e.from);
        if (e.informationKey && from && actionByKey.has(e.to)) checkType(e.informationKey, e.informationName || 'Information', e.informationType, e.informationSystems || [], [from]);
    });
    outputs.filter(o => o.superType === NODE_SUPERTYPE.INFORMATION).forEach(o => checkType(o._key, o.name || 'Unnamed output', o.heldAs ?? o.type, o.heldBy || [], creatorsOf(o)));

    // ----- types LINQ read, the information being typed as plain information: to confirm -----
    const typeReads = new Map<string, { key: string; name: string; modelKey: string; type: NODE_TYPE }>();
    edges.forEach(e => {
        const from = actionByKey.get(e.from);
        if (e.informationKey && e.informationTypeRead && e.informationType !== undefined && from && !typeReads.has(e.informationKey))
            typeReads.set(e.informationKey, { key: e.informationKey, name: e.informationName || 'Information', modelKey: from.modelKey, type: e.informationType });
    });
    outputs.filter(o => o.heldAsRead && o.heldAs !== undefined && !typeReads.has(o._key))
        .forEach(o => typeReads.set(o._key, { key: o._key, name: o.name || 'Unnamed output', modelKey: o.modelKey, type: o.heldAs! }));

    // ----- important information in local tools: across models, or of high value -----
    input.systems.filter(s => s.kind === SYSTEM_KIND.LOCAL).forEach(s => {
        const work = actions.filter(a => systemsOf(a).includes(s.key));
        if (!work.length) return;
        const models = [...new Set(work.map(a => a.modelKey))];
        const made = outputs.filter(o => creatorsOf(o).some(a => work.includes(a)));
        const valuable = made.filter(o => (o.value || 0) > 0 || (o.revenue || 0) > 0 || (o.derivedValue || 0) >= HIGH_DERIVED_VALUE);
        if (models.length < LOCAL_TOOL_MODELS && !valuable.length) return;
        const hours = work.reduce((t, a) => t + (a.hours || 0), 0);
        const reasons = [
            models.length >= LOCAL_TOOL_MODELS ? `is used in ${models.length} models (${list(models.map(modelName))})` : '',
            valuable.length ? `makes ${list(valuable.map(o => o.name))}, ${valuable.length === 1 ? 'which carries' : 'which carry'} value` : '',
        ].filter(r => r);
        rows.push({
            key: `local-${s.key}`, kind: 'local', model: models.length === 1 ? modelName(models[0]) : `${models.length} models`,
            severity: valuable.length && models.length >= LOCAL_TOOL_MODELS ? 'high' : 'medium',
            title: `Important work done in ${s.name}`,
            detail: `${s.name} is a local tool, yet it ${reasons.join(' and ')}: ${number(hours)} hours a year of work. Local tools have no controls around who changes what, `
                + `so information this important belongs in a system of record.`,
            hours, cost: work.reduce((t, a) => t + (a.cost || 0), 0), alsoFriction: false, systemsSuggested: !s.confirmed, context: { kind: 'system', key: s.key, modelKey: ORGANISATION_MODEL_KEY },
            systems: [s.key],
        });
    });

    const order: Record<InformationRiskKind, number> = { bridge: 0, local: 1, copy: 2 };
    rows.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1) || order[a.kind] - order[b.kind] || b.hours - a.hours);
    return {
        rows,
        bridges: rows.filter(r => r.kind === 'bridge'),
        copies: rows.filter(r => r.kind === 'copy'),
        localTools: rows.filter(r => r.kind === 'local'),
        bridgeHours: rows.filter(r => r.kind === 'bridge').reduce((t, r) => t + r.hours, 0),
        rekeying,
        rekeyHours: rekeying.reduce((t, r) => t + r.hours, 0),
        rekeyCost: rekeying.reduce((t, r) => t + r.cost, 0),
        bridgeFrictionHours: bridgeFriction.reduce((t, w) => t + w.hours, 0),    // in the friction total, beside re-keying
        bridgeFrictionCost: bridgeFriction.reduce((t, w) => t + w.cost, 0),
        // the steps said not to add value that a bridge or re-keying counts, which the scorecard leaves out of not value adding
        claimedWasteKeys: new Set([...[...bridgeWork.values()].map(w => w.step), ...rekeying.map(r => actionByKey.get(r.stepKey)!)]
            .filter(notValueAdding).map(s => s._key)),
        handoffs: handoffTypes.size,
        untypedHandoffs,
        untypedHandoffItems,     // which they are
        typeConflicts: [...typeConflicts.values()],
        typeReads: [...typeReads.values()],     // information whose type is LINQ's read   // information whose type doesn't match the systems around it
    };
};
